import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {collectEnumValues} from '../../../../../libs/shared/core/src/models/api/types/enumTypes';
import {StyledRequiredSpan} from '../../../../../libs/shared/ui/src/components/fields/fieldLabel';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {CircleModel} from '../../../../models/api/circleModel';
import {SettingsInboxesNewCreatorState} from '../../../../settings/inboxes/newCreator/settingsInboxesNewCreatorState';
import {OnboardingGuideSlide, OnboardingGuideSlideProps} from '../../guide/onboardingGuideSlide';
import {OnboardingSelectionButton} from '../onboardingSelectionButton';
import {
  StyledColumnFormDescriptionDiv,
  StyledOnboardingFormTitleDiv,
  StyledOnboardingFormTitlesDiv,
} from '../onboardingWelcomeHelpers';

/*
 * Props.
 */

export enum OnboardingWorkflowsAssignRuleAssignAlgorithm {
  LOAD_BALANCING = 'load_balancing',
  ROUND_ROBIN = 'round_robin',
}

interface AssignAlgorithmConstantsData {
  label: ReactNode;
}

const assignAlgorithmConstants: Record<
  OnboardingWorkflowsAssignRuleAssignAlgorithm,
  AssignAlgorithmConstantsData
> = {
  [OnboardingWorkflowsAssignRuleAssignAlgorithm.LOAD_BALANCING]: {
    label: (
      <Trans id="onboardingwelcome-workflows-assign-rule-load-balancing-label">
        Assign to teammates with fewer conversations
      </Trans>
    ),
  },
  [OnboardingWorkflowsAssignRuleAssignAlgorithm.ROUND_ROBIN]: {
    label: (
      <Trans id="onboardingwelcome-workflows-assign-rule-round-robin-label">
        Assign to each person in a repeating sequence
      </Trans>
    ),
  },
};

export interface OnboardingWorkflowsAssignRuleSlideProps
  extends Pick<OnboardingGuideSlideProps, 'navPrimary' | 'navSecondary'> {
  assignAlgorithm: OnboardingWorkflowsAssignRuleAssignAlgorithm | undefined;
  onChangeAssignAlgorithm: (algorithm: OnboardingWorkflowsAssignRuleAssignAlgorithm) => void;
  inboxState: SettingsInboxesNewCreatorState;
  createdCircle: CircleModel | undefined;
}

/*
 * Styles.
 */

const StyledHalfContainer = styled.div`
  flex: 0 0 50%;
`;

const StyledSelectionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  > * {
    width: fit-content;
  }
`;

const StyledContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const StyledCtaWrapper = styled.div`
  font-weight: ${fontWeights.semibold};
  font-size: ${fontSizes.small};
  color: ${(p) => p.theme.greys.shade70};
`;

/*
 * Component.
 */

export const OnboardingWorkflowsAssignRuleSlide: FC<OnboardingWorkflowsAssignRuleSlideProps> = ({
  assignAlgorithm,
  onChangeAssignAlgorithm,
  createdCircle,
  inboxState,
  ...slideProps
}) => {
  const {circleIds} = inboxState;
  const didCreateACircle = Boolean(circleIds.length > 0 && createdCircle);
  return (
    <OnboardingGuideSlide {...slideProps} isLeftContent>
      <StyledHalfContainer>
        <StyledOnboardingFormTitlesDiv>
          <StyledOnboardingFormTitleDiv>
            <Trans id="onboardingwelcome-workflows-assign-rule-title">
              Create a rule to give each conversation an owner
            </Trans>
          </StyledOnboardingFormTitleDiv>
        </StyledOnboardingFormTitlesDiv>
        <StyledColumnFormDescriptionDiv>
          {didCreateACircle && createdCircle?.name ? (
            <>
              <Trans id="onboardingwelcome-workflows-assign-rule-description-1">
                This rule will assign a teammate within the
              </Trans>
              &nbsp;<b>&ldquo;{createdCircle?.name}&rdquo;</b>&nbsp;
              <Trans id="onboardingwelcome-workflows-assign-rule-description-2">
                group as the owner of each new conversation.
              </Trans>
            </>
          ) : (
            <Trans id="onboardingwelcome-workflows-assign-rule-description-default">
              This rule will assign a teammate within your teammate group as the owner of each new
              conversation.
            </Trans>
          )}
        </StyledColumnFormDescriptionDiv>
        <StyledContentWrapper>
          <StyledCtaWrapper>
            <Trans id="onboardingwelcome-workflows-assign-rule-cta">
              How would you like to assign to teammates?<StyledRequiredSpan>*</StyledRequiredSpan>
            </Trans>
          </StyledCtaWrapper>
          <StyledSelectionWrapper>
            {collectEnumValues(OnboardingWorkflowsAssignRuleAssignAlgorithm).map((assignAlgorithmOption) => (
              <OnboardingSelectionButton
                key={assignAlgorithmOption}
                type="radio"
                isChecked={assignAlgorithmOption === assignAlgorithm}
                onClick={() => onChangeAssignAlgorithm(assignAlgorithmOption)}
                label={assignAlgorithmConstants[assignAlgorithmOption].label}
              />
            ))}
          </StyledSelectionWrapper>
        </StyledContentWrapper>
      </StyledHalfContainer>
    </OnboardingGuideSlide>
  );
};
